import React, { useState, useCallback } from 'react';
import './Form.css';

/**
 * Form Component
 * Main form component that handles state management and validation
 * 
 * @param {function} onSubmit - Submit handler: (values: object) => void
 * @param {function} onChange - Change handler: (values: object) => void
 * @param {object} initialValues - Initial form values
 * @param {object} validationSchema - Validation schema (optional)
 * @param {ReactNode} children - Form content (form sub-components)
 * @param {string} className - Additional CSS classes
 */
const Form = ({
  onSubmit,
  onChange,
  initialValues = {},
  validationSchema,
  children,
  className = ''
}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle field value changes
  const handleFieldChange = useCallback((fieldName, value) => {
    const newValues = {
      ...values,
      [fieldName]: value
    };
    
    setValues(newValues);
    
    // Clear error for this field when user starts typing
    if (errors[fieldName]) {
      const newErrors = { ...errors };
      delete newErrors[fieldName];
      setErrors(newErrors);
    }
    
    // Call onChange callback if provided
    if (onChange) {
      onChange(newValues);
    }
  }, [values, errors, onChange]);

  // Handle field blur (mark as touched)
  const handleFieldBlur = useCallback((fieldName) => {
    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  }, []);

  // Validate form
  const validate = useCallback(() => {
    if (!validationSchema) return true;
    
    const newErrors = {};
    
    // Simple validation - can be extended with a validation library
    Object.keys(validationSchema).forEach((fieldName) => {
      const rules = validationSchema[fieldName];
      const value = values[fieldName];
      
      if (rules.required && (!value || value === '')) {
        newErrors[fieldName] = rules.message || `${fieldName} is required`;
      } else if (rules.pattern && value && !rules.pattern.test(value)) {
        newErrors[fieldName] = rules.message || `${fieldName} is invalid`;
      } else if (rules.minLength && value && value.length < rules.minLength) {
        newErrors[fieldName] = rules.message || `${fieldName} must be at least ${rules.minLength} characters`;
      } else if (rules.maxLength && value && value.length > rules.maxLength) {
        newErrors[fieldName] = rules.message || `${fieldName} must be at most ${rules.maxLength} characters`;
      } else if (rules.validate && typeof rules.validate === 'function') {
        const customError = rules.validate(value);
        if (customError) {
          newErrors[fieldName] = customError;
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [values, validationSchema]);

  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    if (e) {
      e.preventDefault();
    }
    
    // Mark all fields as touched
    const allTouched = {};
    Object.keys(values).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);
    
    // Validate form
    const isValid = validate();
    
    if (!isValid) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (onSubmit) {
        await onSubmit(values);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [values, validate, onSubmit]);

  // Form context value
  const formContext = {
    values,
    errors,
    touched,
    isSubmitting,
    handleFieldChange,
    handleFieldBlur,
    setFieldValue: handleFieldChange,
    setFieldError: (fieldName, error) => {
      setErrors(prev => ({
        ...prev,
        [fieldName]: error
      }));
    },
    setFieldTouched: handleFieldBlur
  };

  // Clone children and inject form context
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        formContext,
        ...child.props
      });
    }
    return child;
  });

  return (
    <form 
      onSubmit={handleSubmit}
      className={`form ${className}`.trim()}
      noValidate
    >
      {childrenWithProps}
    </form>
  );
};

export default Form;










